<script setup>
import { ref, onMounted } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import AboutMeForm from '@/Components/Forms/AboutMeForm.vue'

const props = defineProps({
  user: Object,
})

const aboutMeData = ref({
  highlight: '',
  about_me: '',
})

const aboutMeRef = ref(null)
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

// Populate from user prop
onMounted(() => {
  if (props.user?.teacher_profile) {
    aboutMeData.value = {
      highlight: props.user.teacher_profile.highlight || '',
      about_me: props.user.teacher_profile.about_me || '',
    }
  }
})

const updateAboutMe = (newData) => {
  aboutMeData.value = newData
}

const saveAboutMe = async () => {
  if (!aboutMeRef.value.validate()) {
    return
  }

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const response = await fetch('/teacher/settings/about-me', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify(aboutMeData.value)
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        errors.value = data.errors
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    successMessage.value = 'About me information updated successfully!'
    loading.value = false

    // Clear success message after 3 seconds
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <!-- About Me Form -->
      <AboutMeForm
        ref="aboutMeRef"
        :model-value="aboutMeData"
        @update:model-value="updateAboutMe"
        :errors="errors"
        title="About Me"
        description="Share your story and teaching philosophy with potential students"
        :show-shadow="false"
      />

      <!-- Save Button -->
      <div class="flex justify-end pt-6 border-t border-gray-200">
        <button
          @click="saveAboutMe"
          :disabled="loading"
          class="px-8 py-3.5 bg-gradient-to-r from-teal-500 to-teal-800 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-600/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-base"
        >
          <span v-if="loading" class="material-symbols-outlined animate-spin mr-2 text-xl">progress_activity</span>
          {{ loading ? 'Saving...' : 'Save Changes' }}
          <span v-if="!loading" class="material-symbols-outlined ml-2 text-xl">check</span>
        </button>
      </div>
    </div>
  </SettingsLayout>
</template>
